import type { ComponentType } from 'react';
import { canViewField, isFieldLocked } from '@wlb/shared/utils/profileVisibility';
import type { ViewerContext } from '@wlb/shared/utils/profileVisibility';
import { formatDateOfBirth } from '@wlb/shared/utils/dateHelpers';
import { formatWeightDisplay } from '@wlb/shared/utils/weightUtils';
import type { Profile } from '@wlb/shared/types';
import {
  CalendarIcon,
  FlameIcon,
  MapPinIcon,
  TrophyIcon,
  UserIcon,
  WeightScaleIcon,
  type IconProps,
} from '../../components/ui/icons';

export interface ProfileChip {
  key: string;
  /** i18n key for the chip caption. */
  labelKey: string;
  value: string;
  Icon: ComponentType<IconProps>;
  /** Owner-only badge showing the field is not public. */
  locked: boolean;
}

interface ChipDefinition {
  key: string;
  labelKey: string;
  Icon: ComponentType<IconProps>;
  value: (profile: Profile) => string;
  /** Absent for fields the API never restricts. */
  privacy?: (profile: Profile) => string | undefined;
}

const DEFINITIONS: readonly ChipDefinition[] = [
  {
    key: 'gender',
    labelKey: 'profile.infoCard.labels.gender',
    Icon: UserIcon,
    value: profile => profile.gender ?? '',
    privacy: profile => profile.genderPrivacy,
  },
  {
    key: 'country',
    labelKey: 'profile.infoCard.labels.country',
    Icon: MapPinIcon,
    value: profile => profile.country ?? '',
    privacy: profile => profile.countryPrivacy,
  },
  {
    key: 'dateOfBirth',
    labelKey: 'profile.infoCard.labels.dateOfBirth',
    Icon: CalendarIcon,
    value: profile => formatDateOfBirth(profile.dateOfBirth),
    privacy: profile => profile.dateOfBirthPrivacy,
  },
  {
    key: 'totalPoints',
    labelKey: 'profile.infoCard.labels.totalPoints',
    Icon: TrophyIcon,
    value: profile => String(profile.totalPoints ?? 0),
  },
  {
    key: 'currentWeight',
    labelKey: 'profile.infoCard.labels.currentWeight',
    Icon: WeightScaleIcon,
    value: profile => formatWeightDisplay(profile.currentWeight, profile.unit),
    privacy: profile => profile.weightPrivacy,
  },
  {
    key: 'weeklyPoints',
    labelKey: 'profile.infoCard.labels.weeklyPoints',
    Icon: FlameIcon,
    value: profile => String(profile.weeklyPoints ?? 0),
  },
];

/**
 * Builds the info chips for a profile card. A field is dropped when the viewer
 * may not see it, or when it has no value; the owner keeps every field and gets
 * a lock badge on the ones that are not public.
 */
export const buildProfileChips = (
  profile: Profile,
  viewer: ViewerContext,
): ProfileChip[] =>
  DEFINITIONS.reduce<ProfileChip[]>((chips, definition) => {
    const privacy = definition.privacy?.(profile);
    if (!canViewField(privacy, viewer)) return chips;

    const value = definition.value(profile);
    if (!value) return chips;

    chips.push({
      key: definition.key,
      labelKey: definition.labelKey,
      value,
      Icon: definition.Icon,
      locked: Boolean(viewer.isSelf) && isFieldLocked(privacy),
    });
    return chips;
  }, []);
